import React, { useMemo } from "react";
import { Card, Typography } from "antd";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { UserSkillRecord } from "../firebase-config";
import { getSkillDisplayName, skillExpToPercent } from "../utils/skillUtils";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

interface RadarChartProps {
  skills: UserSkillRecord[];
  title?: string;
}

const RadarChart: React.FC<RadarChartProps> = ({
  skills,
  title = "Distribución de habilidades",
}) => {
  const chartData = useMemo(
    () => ({
      labels: skills.map((skill) => getSkillDisplayName(skill.Materia)),
      datasets: [
        {
          label: "Progreso",
          data: skills.map((skill) => skillExpToPercent(skill.Progress)),
          backgroundColor: "rgba(79, 194, 134, 0.18)",
          borderColor: "rgba(79, 194, 134, 0.95)",
          pointBackgroundColor: "rgba(255, 211, 95, 0.95)",
          pointBorderColor: "rgba(47, 43, 116, 0.95)",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "rgba(47, 43, 116, 0.95)",
          borderWidth: 2,
        },
      ],
    }),
    [skills],
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: "rgba(47, 43, 116, 0.14)" },
        grid: { color: "rgba(47, 43, 116, 0.12)" },
        pointLabels: {
          color: "#2f2b74",
          font: {
            size: 12,
            weight: "bold" as const,
          },
        },
        ticks: {
          backdropColor: "rgba(255,255,255,0.72)",
          color: "#2f2b74",
          beginAtZero: true,
          max: 100,
          stepSize: 20,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw}%`,
        },
      },
    },
  };

  return (
    <Card
      className="app-glass"
      style={{
        borderRadius: 22,
        border: "1px solid var(--dashboard-panel-border)",
        boxShadow: "0 12px 32px rgba(10, 19, 53, 0.14)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Typography.Title
        level={5}
        style={{ margin: "0 0 12px", color: "var(--dashboard-header-blue)" }}
      >
        {title}
      </Typography.Title>
      <div style={{ minHeight: 340, height: 340 }}>
        <Radar data={chartData} options={chartOptions} />
      </div>
    </Card>
  );
};

export default RadarChart;
